import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Minus, Sparkles, RefreshCw } from 'lucide-react';

/**
 * Iter 114 · Gate 2 · Movement Bridge panel.
 * Deterministic revenue/cost/margin bridge for a period vs a comparison
 * period. Numbers are computed by `services/movement_bridge_service.py`.
 * The "What moved" narrative (Gate 3) is phrased by Claude Haiku on top
 * of these exact numbers with a strict "never invent" contract; on any
 * LLM failure the panel silently falls back to the raw bridge tables.
 */

const fmt = (n) => (n === null || n === undefined
  ? '—'
  : `₹${Math.round(n).toLocaleString('en-IN')}`);
const fmtPct = (n) => (n === null || n === undefined ? '—' : `${n.toFixed(1)}%`);
const fmtPt = (n) => (n === null || n === undefined
  ? '—'
  : `${n >= 0 ? '+' : ''}${n.toFixed(1)}pt`);

const RatioBadge = ({ ratio }) => {
  const cfg = {
    improved: { cls: 'bg-green-100 text-green-800', icon: TrendingDown },
    worsened: { cls: 'bg-red-100 text-red-800', icon: TrendingUp },
    held: { cls: 'bg-gray-100 text-gray-700', icon: Minus },
  }[ratio] || { cls: 'bg-gray-100 text-gray-700', icon: Minus };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cfg.cls}`}>
      <Icon size={12} /> {ratio}
    </span>
  );
};

export default function MovementsBridge({ clientId, API, start, end }) {
  const [compare, setCompare] = useState('yoy');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!clientId || !start || !end) return;
    setLoading(true);
    setErr(null);
    const url = `${API}/movements/${clientId}/bridge?compare=${compare}&start=${start}&end=${end}&with_narrative=true`;
    axios.get(url)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [clientId, API, compare, start, end]);

  // Refreshing the narrative asks Claude Haiku to phrase again from the
  // same deterministic input; the numbers never change on refresh.
  const [refreshing, setRefreshing] = useState(false);
  const refreshNarrative = async () => {
    if (refreshing || !data) return;
    setRefreshing(true);
    try {
      const url = `${API}/movements/${clientId}/bridge/narrative/refresh?compare=${compare}&start=${start}&end=${end}`;
      const r = await axios.post(url);
      setData(d => ({ ...d, narrative: r.data }));
    } catch (e) {
      setData(d => ({ ...d, narrative: { narrative: null,
        reason: e?.response?.data?.detail || e.message } }));
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) return <div className="text-text-secondary text-sm">Loading bridge…</div>;
  if (err) return <div className="text-red-700 text-sm" data-testid="bridge-error">Bridge error: {err}</div>;
  if (!data) return null;

  const { current, prior, revenue_bridge: rb, channel_bridge, cost_bridge, margin_bridge: mb } = data;

  return (
    <div className="space-y-6" data-testid="movements-bridge">
      {/* Compare-mode pills */}
      <div className="flex space-x-2">
        {[['yoy', 'vs same month last year'], ['mom', 'vs previous month'], ['custom', 'Custom']].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setCompare(k)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              compare === k
                ? 'bg-primary text-white'
                : 'bg-white border border-border text-text-secondary hover:border-primary'
            }`}
            data-testid={`bridge-compare-${k}`}
            disabled={k === 'custom'}
            title={k === 'custom' ? 'Custom range coming soon' : ''}
          >
            {label}
          </button>
        ))}
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="bridge-kpi-row">
        <KpiCard label="Revenue movement" value={fmt(rb.total_delta)} sub={fmtPct(rb.pct_delta)} testid="kpi-rev-delta" />
        <KpiCard label="EBITDA" value={fmt(mb.ebitda_current)} sub={`Δ ${fmt(mb.ebitda_delta)}`} testid="kpi-ebitda" />
        <KpiCard label="EBITDA margin" value={fmtPct(mb.margin_current_pct)} sub={fmtPt(mb.margin_pt_delta)} testid="kpi-margin" />
        <KpiCard label="Bills" value={current.bills ?? '—'} sub={prior.bills ? `prior ${prior.bills}` : ''} testid="kpi-bills" />
        <KpiCard label="Average bill" value={current.avg_bill ? fmt(current.avg_bill) : '—'} sub={prior.avg_bill ? `prior ${fmt(prior.avg_bill)}` : ''} testid="kpi-avg-bill" />
      </div>

      {/* What moved — Gate 3 · Claude Haiku 4.5 narrative over the same
          deterministic numbers rendered below. Never invents. */}
      <Section title="What moved">
        {(() => {
          const n = data.narrative;
          if (n && n.narrative) {
            return (
              <div data-testid="bridge-narrative" className="space-y-2">
                <div className="flex items-start gap-3">
                  <Sparkles size={16} className="mt-0.5 flex-shrink-0" style={{ color: '#B06A2C' }} />
                  <p className="text-sm leading-relaxed" style={{ color: '#2b2b2b' }}>
                    {n.narrative}
                  </p>
                </div>
                <div className="flex items-center justify-between text-xs text-text-muted pl-7">
                  <span>{n.cached ? 'cached' : 'fresh'}
                    {n.generated_at ? ` · ${new Date(n.generated_at).toLocaleString('en-IN')}` : ''}
                  </span>
                  <button
                    onClick={refreshNarrative}
                    disabled={refreshing}
                    data-testid="bridge-narrative-refresh"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-background transition-colors disabled:opacity-40"
                    title="Regenerate the narrative — never changes the numbers, only how they read.">
                    <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
                    {refreshing ? 'refreshing' : 'refresh'}
                  </button>
                </div>
              </div>
            );
          }
          // LLM failure path — never fabricate a paragraph. Tell the user
          // and let the tables below speak for themselves.
          return (
            <div className="text-sm text-text-muted italic" data-testid="bridge-narrative-fallback">
              AI narrative unavailable{n?.reason ? ` (${n.reason})` : ''}.
              The numbers below are the ground truth.
            </div>
          );
        })()}
      </Section>

      {/* Where the growth came from */}
      <Section title="Where the growth came from">
        <table className="w-full text-sm">
          <thead className="text-text-muted text-xs uppercase tracking-wide">
            <tr><th className="text-left py-2">Driver</th><th className="text-right py-2">Rupees</th><th className="text-right py-2">Detail</th></tr>
          </thead>
          <tbody>
            <BridgeRow label="Volume effect" value={rb.volume_effect}
              detail={rb.bills_current && rb.bills_prior ? `${rb.bills_current - rb.bills_prior >= 0 ? '+' : ''}${rb.bills_current - rb.bills_prior} bills × avg ${fmt(rb.avg_bill_prior)}` : 'bills unknown for one period'} />
            <BridgeRow label="Ticket effect" value={rb.ticket_effect}
              detail={rb.avg_bill_current && rb.avg_bill_prior ? `avg ${fmt(rb.avg_bill_prior)} → ${fmt(rb.avg_bill_current)}` : 'avg unknown for one period'} />
            <tr className="border-t-2 border-primary font-semibold">
              <td className="py-2">Total revenue movement</td>
              <td className="py-2 text-right">{fmt(rb.total_delta)}</td>
              <td className="py-2 text-right text-xs text-text-muted">
                {rb.reconciles === true ? 'Volume + Ticket reconcile ✓' : rb.reconciles === false ? 'Reconciliation off — check math' : 'Bills unknown, decomposition N/A'}
              </td>
            </tr>
          </tbody>
        </table>
      </Section>

      {/* Which channel grew */}
      <Section title="Which channel grew">
        <table className="w-full text-sm">
          <thead className="text-text-muted text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left py-2">Channel</th>
              <th className="text-right py-2">Prior</th>
              <th className="text-right py-2">Current</th>
              <th className="text-right py-2">Δ Rupees</th>
              <th className="text-right py-2">Growth</th>
            </tr>
          </thead>
          <tbody>
            {channel_bridge.map(c => (
              <tr key={c.channel} className="border-t border-border" data-testid={`channel-${c.channel.toLowerCase()}`}>
                <td className="py-2 font-medium">{c.channel}</td>
                <td className="py-2 text-right">{fmt(c.prior)}</td>
                <td className="py-2 text-right">{fmt(c.current)}</td>
                <td className={`py-2 text-right ${c.rupee_delta >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {c.rupee_delta >= 0 ? '+' : ''}{fmt(c.rupee_delta)}
                </td>
                <td className="py-2 text-right text-xs">
                  {c.pct_delta === null ? '—' : `${c.pct_delta >= 0 ? '+' : ''}${c.pct_delta.toFixed(1)}%`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* What happened to costs */}
      <Section title="What happened to costs">
        <table className="w-full text-sm">
          <thead className="text-text-muted text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left py-2">Bucket</th>
              <th className="text-right py-2">Prior ₹</th>
              <th className="text-right py-2">Current ₹</th>
              <th className="text-right py-2">Δ ₹</th>
              <th className="text-right py-2">Prior %</th>
              <th className="text-right py-2">Current %</th>
              <th className="text-right py-2">Δ pt</th>
              <th className="text-center py-2">Ratio</th>
            </tr>
          </thead>
          <tbody>
            {cost_bridge.map(r => (
              <tr key={r.label} className="border-t border-border" data-testid={`cost-${r.label.toLowerCase().replace(/ /g, '-')}`}>
                <td className="py-2 font-medium">{r.label}</td>
                <td className="py-2 text-right">{fmt(r.prior)}</td>
                <td className="py-2 text-right">{fmt(r.current)}</td>
                <td className={`py-2 text-right ${r.rupee_delta >= 0 ? 'text-red-700' : 'text-green-700'}`}>
                  {r.rupee_delta >= 0 ? '+' : ''}{fmt(r.rupee_delta)}
                </td>
                <td className="py-2 text-right">{fmtPct(r.prior_pct)}</td>
                <td className="py-2 text-right">{fmtPct(r.current_pct)}</td>
                <td className="py-2 text-right">{fmtPt(r.pt_delta)}</td>
                <td className="py-2 text-center"><RatioBadge ratio={r.ratio} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </div>
  );
}

const KpiCard = ({ label, value, sub, testid }) => (
  <div className="bg-white border border-border rounded-md p-4" data-testid={testid}>
    <div className="text-xs text-text-muted uppercase tracking-wide">{label}</div>
    <div className="text-xl font-semibold text-primary mt-1">{value}</div>
    {sub && <div className="text-xs text-text-muted mt-0.5">{sub}</div>}
  </div>
);

const Section = ({ title, children }) => (
  <div className="bg-white border border-border rounded-md p-6">
    <h3 className="text-lg font-semibold text-primary mb-4">{title}</h3>
    {children}
  </div>
);

const BridgeRow = ({ label, value, detail }) => (
  <tr className="border-t border-border" data-testid={`bridge-row-${label.toLowerCase().replace(/ /g, '-')}`}>
    <td className="py-2 font-medium">{label}</td>
    <td className={`py-2 text-right ${(value ?? 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
      {value === null || value === undefined ? '—' : ((value >= 0 ? '+' : '') + fmt(value))}
    </td>
    <td className="py-2 text-right text-xs text-text-muted">{detail}</td>
  </tr>
);

export const BoundariesPanel = () => (
  <div className="bg-amber-50 border border-amber-200 rounded-md p-6" data-testid="boundaries-panel">
    <h3 className="text-lg font-semibold text-primary mb-3">What this is not</h3>
    <ul className="space-y-2 text-sm text-text-secondary">
      <li>{'· Recoverable rupees (aggregator disputes, supplier overcharges) live in the '}<strong>Reconciliation Centre</strong>{'. Movements shows what changed period-over-period, not what to chase.'}</li>
      <li>{'· Every figure above is computed, not inferred. If a driver cannot be decomposed (e.g. bill count unknown), it reads "—" rather than a guess.'}</li>
      <li>{'· The "What moved" narrative is phrased by Claude Haiku over the numbers shown here — it never invents figures. When AI is unavailable the card says so and hides the paragraph.'}</li>
    </ul>
  </div>
);
